/**
 * Select Form Field Component
 * @module patterns/components/form-and-content/fields/select
 */

import { type App } from '~/app';

import { type SelectItem,type SelectObject } from './interfaces';

const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Select Field Component');

    app.afterMount
        .then(() => {
            const selectFormField = document.querySelector<HTMLElement>('.select-input-field');
            mountSelectFormField(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Select Field Component', error);
        });

    return function destroySelectFormField() {
        unMountSelectFormField(listeners);

        app.logger.debug('[DXA] Select Field Component destroyed');
    };
};

export function mountSelectFormField(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;
    const forms = rootNode.querySelectorAll<HTMLElement>('form');

    for (const form of forms) {
        //TODO: Refactor this loop to be more concise and readable

        const selectFormFields = form.querySelectorAll<HTMLElement>('.select-input-field');

        for (const selectFormField of selectFormFields) {
            // HiddenPlaceHolderText is displayed when no item is selected
            const hiddenPlaceholderText =
				selectFormField.querySelector<HTMLInputElement>('.select-hidden-placeholder')?.value ?? '';
            // HiddenSelect is what Optimizely Forms will ultimate ultimately use for validation/submit value(s)
            const hiddenSelect = selectFormField.querySelector<HTMLSelectElement>('.select-hidden-form-element');
            // Container for the input control and the button visible to user
            const userVisibleDropdownContainer = selectFormField.querySelector<HTMLElement>('div.toggle-select-button');
            // The input control visible to the user
            const userVisibleFormField = selectFormField.querySelector<HTMLElement>('div.selected-input');
            // The button control visable to the user
            const userVisibleButton = selectFormField.querySelector<HTMLElement>('div.selected-input-button');
            // All selectable options within our custom form field
            const formFieldOptions = selectFormField.querySelectorAll<HTMLElement>('li.selected-option');

            if (
                hiddenSelect &&
				userVisibleFormField &&
				formFieldOptions &&
				userVisibleButton &&
				userVisibleDropdownContainer
            ) {
                for (const option of formFieldOptions) {
                    // Each selectable option requires a trigger to update the form field value
                    const toggleOption = () => {
                        const selectedValue = option.dataset.value ?? '';
                        let selectedValues = '';
                        for (const hiddenOption of hiddenSelect.options) {
                            if (selectedValues.length > 0) selectedValues += '|';
                            selectedValues += hiddenOption.selected ? hiddenOption.value : '';
                        }

                        let valueArray = selectedValues ? selectedValues.split('|') : [];

                        if (valueArray.includes(selectedValue)) {
                            // Option deselected
                            valueArray = valueArray.filter((x) => x !== selectedValue);
                            option.querySelector('svg')?.classList.replace('inline-block', 'hidden');
                        } else {
                            // Option selected
                            if (userVisibleFormField.classList.contains('multiple')) {
                                // Multiple select mode: add the value to the list
                                valueArray.push(selectedValue);
                            } else {
                                // Single select mode: unselect all options and overwrite the selected value
                                valueArray = [selectedValue];
                                const allOptionIcons = option.closest('.dropdown-menu')?.querySelectorAll('svg');
                                if (allOptionIcons) {
                                    for (const element of allOptionIcons) {
                                        element.classList.replace('inline-block', 'hidden');
                                    }
                                }
                            }

                            // Display selected icon on the option clicked
                            option.querySelector('svg')?.classList.replace('hidden', 'inline-block');
                        }

                        // Set the hidden select field so Optimizely forms is able to validate and submit the values
                        for (const hiddenOption of hiddenSelect.options) {
                            hiddenOption.selected = valueArray.some((x) => x == hiddenOption.value);
                        }

                        // Strip empty items of any falsy values (false, 0, 0, 0n, "", null, undefined, NaN)
                        valueArray = valueArray.filter(Boolean);

                        // Get display text of selected values to display to the user
                        const textArray = [];
                        for (const textOption of formFieldOptions) {
                            if (valueArray.some((x) => x == textOption.dataset.value)) textArray.push(textOption.textContent?.trim());
                        }

                        // Update what the user sees
                        userVisibleFormField.textContent = textArray.length > 0 ? textArray.join(', ') : hiddenPlaceholderText;
                    };
                    // *******************
                    // END TOGGLE FUNCTION
                    // *******************

                    // Clone and replace existing option tag to remove all event listeners before adding a new event listener
                    // Temporary fix to ensure multiple click events aren't added to the same select dropdowns
                    const optionClone = option.cloneNode(true) as HTMLElement;
                    option.parentNode?.replaceChild(optionClone, option);

                    // Bind toggleOption to option onClick
                    optionClone.addEventListener('click', toggleOption);
                    listeners.push([optionClone, 'click', toggleOption]);

                    // Once the events have been hooked up we can check if the option is meant to be active by default and "click" it
                    // Not an ideal method as the dataset property is hard coded but there's no way to replicate how the BE does it
                    const isSelected = option.dataset.fDefaultValue;
                    if (isSelected?.toLocaleLowerCase() == '"true"') {
                        option.click();
                    }
                }
            }
        }
    }
}

export function refreshSelectData(currentSelect: HTMLElement | null, overrideUrl: string) {
    //const dropdown = document?.querySelector<HTMLElement>('#00687bbc-f94d-48c9-8de8-715d72f72fd3');
    const selectContainer = currentSelect?.parentNode;
    const dropdown = selectContainer?.querySelector<HTMLElement>('.dropdown-menu');
    const defaultSelect = selectContainer?.querySelector<HTMLElement>('select');
    const defaultOptionType = defaultSelect?.dataset.elementType;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetch(overrideUrl)
    // the JSON body is taken from the response
        .then(async (res) => res.json())
        .then((res) => {
            // The response has an `any` type, so we need to cast
            // it to the `User` type, and return it from the promise
            const selectData = mapAsSelectObject(res);
            if (selectData && selectData?.options?.length > 0 && dropdown && defaultSelect) {
                //clear all options and add new
                defaultSelect.innerHTML = '';
                const newOption = document.createElement('option');
                defaultSelect.append(newOption);
                //Clear all custom divs and add li
                dropdown.innerHTML = '';
                for (const currentOption of selectData.options) {
                    const newOption = document.createElement('option');
                    newOption.text = currentOption.label;
                    newOption.value = currentOption.value ?? '';
                    defaultSelect.append(newOption);

                    const newLi = document.createElement('li');
                    newLi.append(document.createTextNode(currentOption.label));
                    newLi.classList.add(
                        'selected-option',
                        'px-3',
                        'py-2.5',
                        'border-b',
                        'border-grey-pastel',
                        'hover:bg-info-500-12',
                        'cursor-pointer'
                    );

                    newLi.dataset.optionText=currentOption?.label ?? '';
                    if(defaultOptionType){
                        newLi.dataset.elementType=defaultOptionType;
                    }

                    newLi.dataset.value = currentOption?.value ?? '';
                    dropdown?.append(newLi);
                }

                //Remove the visible selection as well
                const userVisibleFormField = selectContainer?.querySelector<HTMLElement>('div.selected-input');
                const placeHolderText =
						selectContainer?.querySelector<HTMLInputElement>('.select-hidden-placeholder')?.value ?? '';
                if (userVisibleFormField) userVisibleFormField.textContent = placeHolderText;
                //Remove all the default event listeners as mounting will add it again
                unMountSelectFormField(listeners);
                mountSelectFormField(listeners);
            }
        });
}

const mapAsSelectObject = (flatData: any | null) => {
    const allOptions: SelectObject = {
        name: 'Override',
        options: [],
    };

    if (flatData?.data) {
        for (const [key, value] of Object.entries(flatData.data)) {	const option: SelectItem = {
            label: value as string,
            value: key as string,
        };
        allOptions.options.push(option);
        }

        allOptions.options.sort((a, b) => { return a.label > b.label ? 1 : -1 });
    }

    return allOptions;
};

export function unMountSelectFormField(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}

export const resetSelectField = (form: HTMLElement) => {
    const selectFormFields = form.querySelectorAll<HTMLElement>('.select-input-field');
    for (const selectFormField of selectFormFields) {
        const userVisibleFormField = selectFormField.querySelector<HTMLElement>('div.selected-input');
        const hiddenSelect = selectFormField.querySelector<HTMLSelectElement>('.select-hidden-form-element');
        const hiddenPlaceholderText =
			selectFormField.querySelector<HTMLInputElement>('.select-hidden-placeholder')?.value ?? '';

        if (userVisibleFormField && hiddenPlaceholderText && hiddenSelect) {
            userVisibleFormField.textContent = hiddenPlaceholderText;

            // Set the first option (placeholder) as the selected value
            if (hiddenSelect.options.length > 1) {
                hiddenSelect.options[0].selected = true;
            }
        }
    }
};

export const selectDefaultInSelectElement = (selectElement: HTMLElement,selectText:string) => {
    const dependentEventTriggerElementParent= selectElement.querySelectorAll<HTMLElement>('ul.dropdown-menu li.selected-option');
    for (const element of dependentEventTriggerElementParent) {
        const liElement = element as HTMLLIElement;
        if(liElement.innerHTML?.trim()?.toLowerCase() === selectText.toLowerCase())
        {
            liElement.click();
        }
    }
};
